import { useMemo } from "react";
import BaseDataTable, {
    IDataRow,
} from "../../../components/ui/baseDataTable";
import { ErrorDisplay } from "../../../components/ui/error/ErrorDisplay";
import { CustomLoadingSpinner } from "../../../components/ui/spinners/LoadingSpinner";
import BaseDataTableFrame, {
    BaseDataTableEmptyState,
    BaseDataTableSaveFooter,
} from "../../../components/ui/BaseDataTableFrame";
import {
    DisbursementRow,
    useDisbursementsTableVM,
} from "../Logic/useDisbursementsTableVM";
import { makeDisbursementColumns } from "./disbursementColumns";

export const DisbursementsTable: React.FC = () => {
    const vm = useDisbursementsTableVM();

    const dataRows: IDataRow<DisbursementRow>[] = useMemo(
      () =>
          vm.rows.map((row) => ({
            data: row,
            isEditable: !vm.isSaving,
            widgets: vm.isSaving
                ? []
                : row.isAdded
                ? ["add", "delete"]
                : ["add"],
            onAdd: vm.addRow,
            onDelete: vm.deleteRow,
          })),
      [vm.rows, vm.isSaving, vm.addRow, vm.deleteRow],
    );

    const columnConfigs = makeDisbursementColumns(vm); //[vm] doesn't meaningfully memoize, hence no `useMemo`

    if (vm.isLoading) {
      return (
        <CustomLoadingSpinner
          title="Loading disbursements..."
          subtitle="Fetching disbursement records for this case."
          minHeight="min-h-[160px]"
        />
      );
    }

    if (vm.error) {
      return (
        <ErrorDisplay
          error="Unable to load disbursements. Please try again."
          onRetry={vm.retry}
        />
      );
    }

    return (
      <div className="w-full flex flex-col">
        <BaseDataTableFrame
          title="Disbursements"
          subtitle="Ordered by disbursement date. Records for the same loan account or credit facility may not appear together"
        >
          <BaseDataTable<DisbursementRow>
            columnConfigs={columnConfigs}
            dataRows={dataRows}
            showDefaultTableBody={dataRows.length === 0}
            defaultTableBody={
              <BaseDataTableEmptyState
                title="No disbursement records"
                subtitle="Add disbursement records for this case"
                onAdd={vm.addRow}
              />
            }
          />
        </BaseDataTableFrame>

        {vm.hasChanges && (
          <BaseDataTableSaveFooter
            isSaving={vm.isSaving}
            disabled={vm.hasValidationErrors}
            onSave={vm.save}
            onDiscard={vm.discard}
          />
        )}
      </div>
    );
};
